import { useCallback, useEffect, useMemo, useState } from 'react';
import { COUNT_PRIMARY_GROUPS } from 'framework-listing-lib/internal/MultiLanguageGroup/constants/actions';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';

/**
 * Public useMultiLanguageGroup hook.
 * It proxies all actions via @listingLibObserver down to our internal implementation of multi-language group.
 */
export default function useMultiLanguageGroup() {
  const [multiLangGroupsCount, setMultiLangGroupsCount] = useState(0);
  const handleShowAllMultiLanguageGroups = useCallback(() => {
    listingLibObserver.showAllMultiLanguageGroups();
  }, []);
  const handleShowMultiLanguageGroup = useCallback((primaryGroupObjectId, translatedContentObjectIds) => {
    listingLibObserver.showMultiLanguageGroup(primaryGroupObjectId, translatedContentObjectIds);
  }, []);
  const handleHideAllMultiLanguageGroups = useCallback(() => {
    listingLibObserver.hideAllMultiLanguageGroups();
  }, []);
  const handleHideMultiLanguageGroup = useCallback(primaryGroupObjectId => {
    listingLibObserver.hideMultiLanguageGroup(primaryGroupObjectId);
  }, []);
  const handleRemoveFromMultiLanguageGroup = useCallback((primaryGroupObjectId, translatedContentToRemove) => {
    listingLibObserver.removeFromMultiLanguageGroup(primaryGroupObjectId, translatedContentToRemove);
  }, []);
  const handleMakePrimaryMultiLanguageGroup = useCallback((primaryGroupObjectId, translatedContent) => {
    listingLibObserver.makePrimaryGroup(primaryGroupObjectId, translatedContent);
  }, []);
  const handleMoveToMultiLanguageGroup = useCallback((primaryGroupObjectId, translatedContent) => {
    listingLibObserver.moveToPrimaryGroup(primaryGroupObjectId, translatedContent);
  }, []);

  /**
   * Triggered by @useMultiLanguageGroupObserver when data changes in any of the operations above
   */
  const handleSetPrimaryGroupCount = useCallback(count => {
    setMultiLangGroupsCount(count);
  }, []);
  useEffect(() => {
    listingLibObserver.on(COUNT_PRIMARY_GROUPS, handleSetPrimaryGroupCount);
    return () => {
      listingLibObserver.off(COUNT_PRIMARY_GROUPS, handleSetPrimaryGroupCount);
    };
  }, [handleSetPrimaryGroupCount]);
  const value = useMemo(() => ({
    hideAllMultiLanguageGroups: handleHideAllMultiLanguageGroups,
    hideMultiLanguageGroup: handleHideMultiLanguageGroup,
    makePrimaryMultiLanguageGroup: handleMakePrimaryMultiLanguageGroup,
    moveToMultiLanguageGroup: handleMoveToMultiLanguageGroup,
    multiLangGroupsCount,
    removeFromMultiLanguageGroup: handleRemoveFromMultiLanguageGroup,
    showAllMultiLanguageGroups: handleShowAllMultiLanguageGroups,
    showMultiLanguageGroup: handleShowMultiLanguageGroup
  }), [handleHideAllMultiLanguageGroups, handleHideMultiLanguageGroup, handleMakePrimaryMultiLanguageGroup, handleMoveToMultiLanguageGroup, handleRemoveFromMultiLanguageGroup, handleShowAllMultiLanguageGroups, handleShowMultiLanguageGroup, multiLangGroupsCount]);
  return value;
}